import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
    Activity,
    Users,
    FileText,
    Newspaper,
    Image as ImageIcon,
    BarChart3,
    Calculator,
    Mail,
    MessageSquare,
    AlertCircle,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, Tooltip } from "recharts";

const CARD_DEFS = [
    { key: "blog_posts", icon: FileText, label: "Blog Yazıları", color: "teal", to: "/admin/blog" },
    { key: "sector_data", icon: BarChart3, label: "Sektör Verileri", color: "deep-blue", to: "/admin/veri-merkezi" },
    { key: "city_data", icon: Activity, label: "İl Verileri", color: "gold", to: "/admin/il-verileri" },
    { key: "calculator_config", icon: Calculator, label: "Araçlar", color: "teal", to: "/admin/araclar" },
    { key: "media_library", icon: ImageIcon, label: "Medya", color: "deep-blue", to: "/admin/medya" },
    { key: "newsletter_subscribers", icon: Mail, label: "Aboneler", color: "gold", to: "/admin/newsletter" },
    { key: "contact_requests", icon: MessageSquare, label: "Mesajlar", color: "teal", to: "/admin/iletisim" },
    { key: "admin_users", icon: Users, label: "Adminler", color: "deep-blue", to: "/admin/guvenlik" },
];

export default function AdminDashboard() {
    const { t } = useTranslation();
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard/stats");
                setStats(data);
            } catch (_) {
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy">{t("admin.dashboard")}</h1>
                    <p className="text-sm text-deep-blue/70">Otel Pro · {t("tagline")}</p>
                </div>
                {(stats.unread_contacts || 0) > 0 && (
                    <Link to="/admin/iletisim" className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full bg-data-red/10 text-data-red border border-data-red/20" data-testid="dashboard-unread-badge">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {stats.unread_contacts} okunmamış mesaj
                    </Link>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {CARD_DEFS.map((c) => {
                    const Icon = c.icon;
                    const value = stats.counts?.[c.key] ?? 0;
                    const colorCls = c.color === "teal" ? "bg-teal/15 text-teal-700" : c.color === "gold" ? "bg-gold/15 text-gold-700" : "bg-deep-blue/10 text-deep-blue";
                    return (
                        <Link
                            key={c.key}
                            to={c.to}
                            data-testid={`stat-card-${c.key}`}
                            className="lift-card bg-white rounded-lg shadow-md hover:shadow-xl border border-slate-100 p-4"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-xs uppercase tracking-wider text-deep-blue/70 font-semibold">{c.label}</span>
                                <span className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${colorCls}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                            </div>
                            <div className="mt-2 font-data text-3xl font-bold text-dark-navy">
                                {loading ? "—" : (value || 0).toLocaleString("tr-TR")}
                            </div>
                        </Link>
                    );
                })}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue/80">Newsletter (7 gün)</h2>
                        <span className="text-xs font-data text-teal-700 font-semibold">+{stats.newsletter_trend?.reduce((a, b) => a + (b.count || 0), 0) || 0}</span>
                    </div>
                    <div className="h-36" data-testid="dashboard-newsletter-trend">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={stats.newsletter_trend || []}>
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                <Line type="monotone" dataKey="count" stroke="#39c8c1" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue/80">İletişim (7 gün)</h2>
                        <span className="text-xs font-data text-gold-700 font-semibold">+{stats.contact_trend?.reduce((a, b) => a + (b.count || 0), 0) || 0}</span>
                    </div>
                    <div className="h-36" data-testid="dashboard-contact-trend">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={stats.contact_trend || []}>
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                <Line type="monotone" dataKey="count" stroke="#d6b06b" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <Panel title="Son Aboneler" data={stats.recent_subscribers} render={(s) => (
                    <>
                        <div className="text-dark-navy font-semibold">{s.email}</div>
                        <div className="text-xs text-slate-500 font-data">{s.source}</div>
                    </>
                )} />
                <Panel title="Son Mesajlar" data={stats.recent_contacts} render={(c) => (
                    <>
                        <div className="text-dark-navy font-semibold">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.email} · {c.subject}</div>
                    </>
                )} />
                <Panel title="Güvenlik Olayları" data={stats.recent_security_logs} render={(l) => (
                    <>
                        <div className={`font-semibold ${l.event_type === "login_failed" ? "text-data-red" : "text-dark-navy"}`}>{l.event_type}</div>
                        <div className="text-xs text-slate-500">{l.ip_address || "-"}</div>
                    </>
                )} />
            </div>
        </div>
    );
}

function Panel({ title, data, render }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue/80 mb-3">{title}</h2>
            <div className="divide-y divide-slate-100">
                {(data || []).length === 0 && <div className="text-sm text-slate-500 py-3">Kayıt yok.</div>}
                {(data || []).map((d, i) => (
                    <div key={i} className="py-2.5 flex items-start justify-between gap-4 text-sm">
                        <div className="min-w-0 truncate">{render(d)}</div>
                        <div className="text-xs text-slate-500 font-data shrink-0">
                            {new Date(d.created_at || d.registered_at || Date.now()).toLocaleDateString("tr-TR")}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
